// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { CustomFieldApiKeyField, CustomFieldPermissionField, CustomFieldPreview, CustomFieldTypeSelect } from ".";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import createFieldFormatterDecorator from "final-form-calculate";
import createFocusDecorator from "final-form-focus";
import { camelCase, capitalize, noop, pick } from "lodash";
import { useState } from "react";
import * as React from "react";
import { Field, Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";

import {
  Button,
  FormField,
  Heading,
  Input,
  Modal,
  ModalPortal,
  SpacingToken,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { TeamsSelect } from "components/teams/team-multi-select/TeamsSelect";
import { CustomFieldTypes } from "constants/custom-fields";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { customFieldActions } from "store/entities/custom-fields/actions";
import {
  IWithCustomFieldsState,
  selectCustomFieldById,
  selectCustomFieldsList,
} from "store/entities/custom-fields/selectors";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";
import { capitalizeFirstLetter } from "utils/string";
import { compose, maxLength, nonSpecialChars, required, uniqueValue } from "utils/validators";

export enum CUSTOM_FIELDS_MODAL_MODES {
  CREATE = "create",
  EDIT = "edit",
}

type ICustomFieldModalProps = {
  mode: CUSTOM_FIELDS_MODAL_MODES;
  closeModal: () => void;
  id: string;
};

const fieldFormatter = createFieldFormatterDecorator({
  field: "displayName",
  updates: {
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    apiKey: (value: any) => camelCase(value),
  },
});

const modalStyles = css({ width: "800px", maxHeight: "90%", overflow: "auto" });
const focusOnError = createFocusDecorator();
const decorators = [focusOnError];

export const CustomFieldModal: React.FC<ICustomFieldModalProps> = ({ closeModal, mode, id }) => {
  const customFieldsApiKeys = useSelector(selectCustomFieldsList).map(field => field.apiKey);
  const customField = useSelector(state => selectCustomFieldById(state as IWithCustomFieldsState, id));
  const initialValue = { ...customField, roleLevel: customField?.roleLevel || "normal" };
  const [isTeamsSelectOpen, setTeamsSelectOpen] = useState<boolean>(false);
  const dispatch = useDispatch();

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleTeamsSelectToggle = (isOpen: boolean) => {
    setTeamsSelectOpen(isOpen);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function handleCloseModalButtonClick() {
    trackEngagement(`Custom fields: ${capitalize(mode)} custom field modal closed`, EventPlace.Settings);
    closeModal();
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  const handleSubmitForm = (customField: any, form: any) => {
    if (mode === CUSTOM_FIELDS_MODAL_MODES.CREATE) {
      dispatch(customFieldActions.create({ ...customField, status: "active" }));
    } else if (mode === CUSTOM_FIELDS_MODAL_MODES.EDIT) {
      const updatedFields = pick(customField, Object.keys(form.getState().dirtyFields));
      dispatch(
        customFieldActions.update({
          id,
          ...updatedFields,
        })
      );
    }
    closeModal();
  };

  if (mode === CUSTOM_FIELDS_MODAL_MODES.CREATE && decorators.length === 1) {
    decorators.push(fieldFormatter);
  } else if (mode === CUSTOM_FIELDS_MODAL_MODES.EDIT && decorators.length === 2) {
    decorators.pop();
  }
  return (
    <Block margin="20px 30px">
      <Form
        onSubmit={handleSubmitForm}
        decorators={decorators}
        initialValues={pick(initialValue, ["displayName", "type", "apiKey", "roleLevel", "teamIDs"])}
      >
        {/* TODO: Fix this the next time the file is edited. */}
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {({ handleSubmit, valid, values, pristine }: any) => (
          <ModalPortal zIndex={StackingContextLevel.Modal}>
            <Modal
              closeOnOverlayPress={false}
              onClose={handleCloseModalButtonClick}
              heading={`${capitalize(mode)} custom field`}
              css={modalStyles}
              footer={
                <Flex>
                  <Block marginRight="8px">
                    <Button
                      kind="secondary"
                      type="button"
                      onClick={handleCloseModalButtonClick}
                      data-testid="custom-field-cancel"
                    >
                      Cancel
                    </Button>
                  </Block>
                  <Button
                    kind="primary"
                    type="submit"
                    form="custom-field-modal-form"
                    disabled={!valid || pristine}
                    data-testid="custom-field-submit"
                  >
                    {mode === CUSTOM_FIELDS_MODAL_MODES.CREATE ? "Create" : "Save"}
                  </Button>
                </Flex>
              }
            >
              <Flex justifyContent="space-around" gap={withVar(SpacingToken.Spacing6)}>
                <form id="custom-field-modal-form" onSubmit={handleSubmit}>
                  <Block width="360px">
                    <Field
                      validate={compose(
                        required("Name is required"),
                        maxLength("Name is too long (max 64 characters)", 64)
                      )}
                      name="displayName"
                    >
                      {/* TODO: Fix this the next time the file is edited. */}
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      {({ input, meta }: any) => (
                        <FieldWrapper labelText="Display name" error={meta.touched && meta.error}>
                          <Input
                            {...input}
                            id="custom-field-display-name"
                            error={meta.error && meta.touched}
                            placeholder="Name a custom field, e.g. order number"
                            data-testid="custom-field-display-name"
                            autoFocus
                          />
                        </FieldWrapper>
                      )}
                    </Field>
                    <Field
                      validate={compose(required("Type is required"))}
                      disabled={mode === CUSTOM_FIELDS_MODAL_MODES.EDIT}
                      id="custom-field-type-select"
                      name="type"
                      size="big"
                    >
                      {/* TODO: Fix this the next time the file is edited. */}
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      {({ id, input, meta }: any) => (
                        <FieldWrapper labelText="Type" error={meta.touched && meta.error}>
                          <CustomFieldTypeSelect
                            id={id}
                            input={input}
                            meta={meta}
                            disabled={mode === CUSTOM_FIELDS_MODAL_MODES.EDIT}
                          />
                        </FieldWrapper>
                      )}
                    </Field>
                    <Field
                      validate={compose(required("Team is required"))}
                      id="custom-field-team-select"
                      name="teamIDs"
                      labelText="Assign to teams"
                    >
                      {/* TODO: Fix this the next time the file is edited. */}
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      {({ input, meta, id }: any) => (
                        <TeamsSelect
                          labelText="Teams with access"
                          placeholder="Select who can see custom field values"
                          filterValues={values.teamIDs}
                          onChange={input.onChange}
                          isOpen={isTeamsSelectOpen}
                          toggleOpen={handleTeamsSelectToggle}
                          meta={meta}
                          id={id}
                        />
                      )}
                    </Field>
                    <Flex column>
                      <Heading size="xs">Who can change the custom field value?</Heading>
                      <Field
                        type="radio"
                        value="owner"
                        id="custom-field-permission-admins-only"
                        name="roleLevel"
                        // FIXME: Fix this the next time the file is edited.
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        component={({ input }: any) => (
                          <CustomFieldPermissionField
                            onChange={input.onChange}
                            value={input.value}
                            checked={!!input.checked}
                          />
                        )}
                      />
                      <Field
                        type="radio"
                        value="normal"
                        id="custom-field-permission-admins-and-agents"
                        name="roleLevel"
                        // FIXME: Fix this the next time the file is edited.
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        component={({ input }: any) => (
                          <CustomFieldPermissionField
                            onChange={input.onChange}
                            value={input.value}
                            checked={!!input.checked}
                          />
                        )}
                      />
                      <Field
                        type="radio"
                        value="readOnly"
                        id="custom-field-permission-api-only"
                        name="roleLevel"
                        // FIXME: Fix this the next time the file is edited.
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        component={({ input }: any) => (
                          <CustomFieldPermissionField
                            onChange={input.onChange}
                            value={input.value}
                            checked={!!input.checked}
                          />
                        )}
                      />
                    </Flex>
                    <Field
                      id="custom-fieled-api-key-name"
                      name="apiKey"
                      labelText="API Key name"
                      validate={compose(
                        required("API key name is required"),
                        maxLength("API key name is too long", 32),
                        nonSpecialChars("API key name can't contain special characters and spaces"),
                        mode === CUSTOM_FIELDS_MODAL_MODES.CREATE
                          ? uniqueValue(
                              "This name is used for another custom field. Try with a different name",
                              customFieldsApiKeys
                            )
                          : noop
                      )}
                    >
                      {/* TODO: Fix this the next time the file is edited. */}
                      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                      {({ input, meta, id }: any) => (
                        <CustomFieldApiKeyField
                          input={input}
                          meta={meta}
                          id={id}
                          disabled={mode === CUSTOM_FIELDS_MODAL_MODES.EDIT}
                        />
                      )}
                    </Field>
                  </Block>
                </form>
                {/* @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message */}
                <CustomFieldPreview customFieldType={CustomFieldTypes[capitalizeFirstLetter(values?.type)]} />
              </Flex>
            </Modal>
          </ModalPortal>
        )}
      </Form>
    </Block>
  );
};

const FieldWrapper = styled(FormField)`
  width: 100%;
`;
